<template lang="pug">	
	.ssml-insertion--pronunciation-tool(v-if="popupVisible")
		Popup(:popupVisible.sync="popupVisible", :width="480", :height="420", :title="$tCaption('Pronunciation tool')", @hiding="() => { stopAudio(); stopRecording(true); }", :closeOnOutsideClick="false")
			.flex-col.space-medium.ssml-insertion--pronunciation-tool 
				.flex-row.align-center.space(v-if="isLoading")
					Loader(text="")
					span {{$tCaption('Searching variants for "SEARCHING_TEXT"').replace(/SEARCHING_TEXT/g, selectedText)}}
				.flex-col.space-medium(v-if="!isLoading")
					.flex-row.align-center
						.adt-tab(:class="{ active: tab === 'openai' }", @click="tab = 'openai'") {{$tCaption('AI Suggestions')}}
						.adt-tab(v-if="msVariants", :class="{ active: tab === 'ms' }", @click="tab = 'ms'") {{$tCaption('IPA Recording')}}
						.adt-tab(:class="{ active: tab === 'stressipa' }", @click="tab = 'stressipa'") {{$tCaption('Stress')}}
					.flex-col.space
						span(v-if="activeVariants.length") {{$tCaption('Variants for "SEARCHING_TEXT":').replace(/SEARCHING_TEXT/g, selectedText)}}
						.flex-col.space(v-if="activeVariants.length")
							.flex-row.justify.align-center.space-large(v-for="(variant, idx) in activeVariants", @click="selectedVariant = variant; selectedOptionKind = tab")
								.font-lg.variant.clickable.hoverable(:class="{ 'accent-color selected': selectedVariant === variant }", v-html="`${idx + 1}. ${variant}`")
								.clickable.hoverable.flex-row.align-center.center.adt-button.rounded.padded-lg(@click="playVariant(variant)")
									fas(size="xs", :icon="isVariantLoading === variant ? 'spinner' : (playingVariant === variant ? 'stop' : 'play')", :spin="isVariantLoading === variant")
						.grey.font-lg(v-else-if="tab === 'openai'") {{$tCaption('Service not responding, try again later.')}}
						.grey.font-lg(v-else-if="tab === 'ms'") 
							span(v-if="msVariants && msVariants.error") {{$tCaption('Language not supported.')}}
							span(v-else) {{$tCaption('We could not recognize the example provided. Please try again.')}}
			.ssml-insertion--pronunciation-tool.flex-row.align-center.space(slot="actions-left")
				.clickable.hoverable.flex-row.align-center.center.adt-button.rounded.padded-xlg.record-button(@click="toggleRecord", :title="isRecording ? $tCaption('Stop') : $tCaption('Record pronunciation')", 
					:class="{ recording: isRecording, 'disabled-controls': isLoading }")
					fas(size="lg", :icon="recorder.processing ? 'spinner' : (isRecording ? 'square' : 'fas fa-microphone fa-xs')", :spin="recorder.processing")
				.flex-row.space-large.align-center
					DxCheckBox(:value.sync="recorder.listeningMic.value", :text="$tCaption('Mic preview')")
					VuMeter(ref="vumeter", orientation="horizontal", :isPlaying="isRecording || recorder.listeningMic.value", :filters="{ stereoAnalyser: recorder.stereoAnalyser }", :compact="true", size="80px")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="() => { stopRecording(true); popupVisible = false; }")
				DxButton(:text="$tCaption('OK', '@actions')", type="default", @click="save", :disabled="!selectedVariant")
</template>

<script>
	import { saveAs } from 'file-saver';
	import Popup from 'app-core/Popup.vue';
	import DxButton from 'devextreme-vue/button';
	import Loader from 'app-core/Loader.vue';
	import helper from 'view/creative/EasyCreative/helper';
	import recorderWorker from 'worker-loader!recorder/recorderWorker.js';
	import Recorder from 'recorder/recorder';
	import audioHelper from 'view/audioTracker/audio-helper';
	import storage from 'storage';
	import DxCheckBox from 'devextreme-vue/check-box';
	import VuMeter from 'view/audioTracker/AudioTrackerVUMeter.vue';

	export default {
		translationCategory: 'insertion.PronunciationTool',
		name: 'ssml-insertion--pronunciation-tool',
		props: ['text', 'voice', 'target', 'language'],
		components: {
			Popup, DxButton, Loader, DxCheckBox, VuMeter
		},
		data() {
			return {
				popupVisible: false,
				isLoading: false,
				selectedVariant: null,
				variants: [],
				playingVariant: null,
				isVariantLoading: null,
				selectedText: null,
				isRecording: false,
				recorder: {
					audioContext: new AudioContext(),
					instance: null,
					stream: null,
					processing: false,
					listeningMic: storage.prop('pronunciation_listeningMic', void 0, false),
					input: null,
					stereoAnalyser: null
				},
				msVariants: null,
				tab: 'openai',
				selectedOptionKind: 'openai',
				stressVariants: []
			};
		},
		computed: {
			activeVariants() {
				return this.tab === 'stressipa' ? this.stressVariants.map(f => f.text) : (this.tab === 'openai' ? this.variants : this.msVariants?.candidates?.map(f => f.pronunciation) || []);
			},
			locale() {
				return this.language || this.voice?.language?.locale || 'en-US';
			}
		},
		watch: {
			'recorder.listeningMic.value'() {
				this.initRecording(true);
			},
			'tab'(v) {
				if (v === 'stressipa' && !this.stressVariants?.length) {
					this.getStressVariants();
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.ssmlTextPronunciationTool = this;
			document.addEventListener('selectionchange', this.calcSelectionText);
		},
		destroyed() {
			this.stopAudio();
			delete this.$lastSelection;
			document.removeEventListener('selectionchange', this.calcSelectionText);
		},
		methods: {
			initRecording(preventRecord) {
				this.stopRecording(true);
				var constraints = { audio: {
					mandatory: {
						googEchoCancellation: false
					},
					optional: []
				}, video: false };
				return new Promise(res => navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
					this.recorder.stream = stream;
					var ctx = this.recorder.audioContext;
					var input = ctx.createMediaStreamSource(stream);

					this.recorder.input = input;
					
					/* Stereo analyser */
					var stereoAnalyser = new audioHelper.StereoAnalyserNode(ctx);
					stereoAnalyser.fftSize = 512;
					this.recorder.stereoAnalyser = stereoAnalyser;

					if (this.recorder.listeningMic.value) {
						input.connect(stereoAnalyser);
						stereoAnalyser.connect(ctx.destination);
					} else {
						input.connect(stereoAnalyser);
					}

					this.recorder.instance = new Recorder(input, { numChannels: 1, bufferLen: this.$globals.recorderJs.bufferLen }, recorderWorker);

					if (!preventRecord) {
						this.recorder.instance.record();
					}

					setImmediate(() => {
						res();
					});
				}).catch((ex) => {
					res();
					throw(ex);
				}));
			},
			async stopRecording(preventSave) {
				if (this.recorder.instance) {
					this.recorder.stream.getAudioTracks()[0].stop();
					this.recorder.instance.stop();
					this.recorder.input.disconnect();
				}

				if (!preventSave) {
					return new Promise((res) => this.recorder.instance.exportWAV((blob) => {
						const resampled = audioHelper.resampleBlob(blob, 16000, { mono: true });
						return res(resampled);
					}));
				}
			},
			async toggleRecord() {
				if (!this.isRecording) {
					this.initRecording();
				} else {
					try {
						this.recorder.processing = true;
						const blob = await this.stopRecording();
						if (this.$route.query.debug === 'true') {
							saveAs(blob, 'test.wav');
						}
						this.msVariants = await this.getMSPronunciation(blob);
						this.tab = 'ms';
						if (__DEBUG__)
							console.debug('Record stop');
					} finally {
						this.recorder.processing = false;
					}
				}
				this.isRecording = !this.isRecording;
			},
			getStressValue(text) {
				return this.stressVariants.find(f => f.text === text)?.value;
			},
			async playVariant(variant) {
				if (__DEBUG__)
					console.debug('Play variant', { variant });

				if (this.isVariantLoading) return;

				let hasBeenPlaying = this.playingVariant === variant;
				this.stopAudio();

				if (hasBeenPlaying) {
					this.playingVariant = null;
					return;
				}

				try {
					this.isVariantLoading = variant;
					variant = `${variant}`.replace(/-/g,'');
					if (this.tab === 'ms') {
						variant = `<phoneme alphabet="${(this.msVariants.phoneSet || 'ipa').toLowerCase()}" ph="${variant}">${(this.selectedText || '').trim()}</phoneme>`
					}
					if (this.tab === 'stressipa') {
						variant = `<phoneme alphabet="ipa" ph="${this.getStressValue(variant)}">${(this.selectedText || '').trim()}</phoneme>`
					}
					const audio = await helper.generateSSML({
						Ssml: `<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${this.locale}">
								<voice name="${this.voice?.id || 'en-US-JennyNeural'}" xml:lang="${this.locale}">
									<mstts:silence type="Leading-exact" value="0ms"></mstts:silence>
									<prosody rate="1" pitch="+0%">
										${variant}
									</prosody>
									<mstts:silence type="Tailing-exact" value="0ms"></mstts:silence>
								</voice>
							</speak>`,
						Rate: 1,
						Language: this.locale,
						Voice: this.voice?.id || 'en-US-JennyNeural',
						JsonConfig: {},
						TtsProviderType: this.voice?.voice?.TtsProviderType
					})

					if(!audio.base64) return;

					this.$player = new Audio();
					this.$player.src = audio.base64;
					this.$player.play();
					this.$player.addEventListener('ended', () => this.playingVariant = null, false);
					this.playingVariant = variant;
				} finally {
					this.isVariantLoading = null;
				}
			},
			stopAudio() {
				if (this.$player) {
					this.$player.pause();
				}
				this.playingVariant = null;
			},
			show() {
				this.popupVisible = false;
				this.selectedVariant = null;
				this.variants = [];
				this.selectedText = (this.text || '').trim();
				this.tab = 'openai';
				this.selectedOptionKind = 'openai';
				this.msVariants = null;
				this.stressVariants = [];
				this.getVariants();
				if (this.recorder.listeningMic.value) {
					this.initRecording(true);
				}
				this.$nextTick(() => this.popupVisible = true);
			},
			save() {
				this.popupVisible = false;
				if (this.selectedOptionKind === 'stressipa') {
					this.$emit('save-phoneme', this.getStressValue(this.selectedVariant), this.selectedText, 'ipa');
				} else if (this.selectedOptionKind === 'ms') {
					this.acceptPronunciation();
					this.$emit('save-phoneme', this.selectedVariant, this.selectedText, this.msVariants?.phoneSet);
				} else {
					this.$emit('save', this.selectedVariant, this.selectedText);
					this.onPronunciationToolSave(this.selectedVariant);
				}
			},
			async getVariants() {
				await this.restoreSelection();
				this.isLoading++;
				const results = await this.$Data.postExternalApi('OpenAiGpt3/GenerateFromPromptWithParameter', {
					promptName: 'Phonetics writer',
					clientIp: this.$auth.userIP(),
					parameter: this.selectedText,
					locale: this.locale,
					lang: this.voice?.language?.name || 'English'
				}).finally(() => this.isLoading--);

				if (results?.Data?.Content) {
					try {
						const bracketsData = (results?.Data?.Content?.replace(/[\r\n]/g,' ').match(/\[(.*?)\]/g) || [])[0] || results?.Data?.Content;
						this.variants = JSON.parse(bracketsData);
					} catch {
						this.variants = [];
					}
				}
			},
			async getStressVariants() {
				await this.restoreSelection();
				this.isLoading++;
				const results = await this.$Data.postExternalApi('OpenAiGpt3/Run', {
					promptCode: 'WORDSTRESSIPA',
					clientIp: this.$auth.userIP(),
					parameters: {
						language: this.locale,
						word: this.selectedText
					}
				}).finally(() => this.isLoading--);

				if (results?.Data?.Content) {
					try {
						this.stressVariants = (results?.Data?.Content || '').split('\n').map(f => {
							let split = (f || '').split('|');
							return {
								text: split[0],
								value: (split[1] || '').replace(/[\/\\]/g, '').trim()
							}
						}).filter(f => f.value)
					} catch {
						this.stressVariants = [];
					}
				}
			},
			async getMSPronunciation(blob) {
				this.isLoading++;
				return this.$Data.postExternalApi('AzurePronunciationLearning/Execute', {
					Url: 'https://pronunciation-learning-api.azurewebsites.net/get_pronunciation',
					Method: 0,
					Body:{
						locale: this.locale,
						data: this.$Data.splitDataUri(await this.$Data.blob2Base64(blob))?.base64
					}
				}).finally(() => this.isLoading--);
			},
			async acceptPronunciation() {
				if (__DEBUG__)
					console.debug('Accept pronunciation');

				return this.$Data.postExternalApi('AzurePronunciationLearning/Execute', {
					Url: 'https://pronunciation-learning-api.azurewebsites.net/accept_pronunciation',
					Method: 0,
					Body:{
						locale: this.locale,
						word: this.selectedText,
						pronunciation: this.selectedVariant
					}
				});
			},
			onPronunciationToolSave(text) {
				this.restoreSelection();
				setImmediate(() => {
					if (this._isDestroyed) return;
					const sourceText = this.selectedText || '';
					const newText = sourceText.replace((sourceText || '').trim(), (text || '').trim());
					if (__DEBUG__)
						console.debug('onPronunciationToolSave', { text, sourceText, newText });
					this.replaceSelectedText(newText);
				});
			},
			calcSelectionText(event) {
				if (this.popupVisible) return;
				if (event && !event?.target?.activeElement?.classList?.contains('dx-texteditor-input')) {
					this.selectedText = null;
					return;
				}
				if (__DEBUG__)
					console.debug('OnSelectionChange', { event });
				var text = "";
				if (window.getSelection) {
					text = window.getSelection().toString();
				} else if (document.selection && document.selection.type != "Control") {
					text = document.selection.createRange().text;
				}
				this.selectedText = text;
				this.$emit('selection-change', event, text);
				this.$lastSelection = {
					target: document.activeElement,
					start: document.activeElement?.selectionStart,
					end: document.activeElement?.selectionEnd,
					text
				};
				return text;
			},
			async restoreSelection() {
				if (this.text) return;
				return new Promise((res) => {
					if (this.$lastSelection?.target) {
						this.$lastSelection?.target.focus();
						this.$lastSelection?.target.setSelectionRange(this.$lastSelection.start, this.$lastSelection.end);
						this.selectedText = this.$lastSelection?.text;
					}
					setImmediate(() => res());
				});
			},
			replaceSelectedText(replacementText) {
				if (this.$lastSelection?.target) {
					const value = this.$lastSelection.target.value || '';
					const newText = value.substr(0, this.$lastSelection.start) + replacementText + value.substr(this.$lastSelection.end, value.length);
					if (__DEBUG__)
						console.debug('Replace selected text', { newText, value })
					this.$lastSelection.target.value = newText;
					this.$emit('change', newText);
				}
			},
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.ssml-insertion--pronunciation-tool {
		.variant {
			b {
				color: $base-accent;
				position: relative;

				&:before {
					content: '`';
					position: absolute;
					left: 45%;
					top: -6px;
					transform: scaleX(-1);
				}
			}
		}
	}
</style>
